import React, {useEffect, useState} from 'react';
import cx from 'classnames';
import {format} from 'date-fns';
import NextLink from 'next/link';

import {useManagePlanQuery} from '#apollo/schema';
import {ProductCategoryEnum} from '#apollo/schema';
import {Button, Link, Text} from '#component/base';
import {UpdateConfirmationModal} from '#component/page/Profile/components/ManagePlanTab/SubscriptionManager/components/SubscriptionManagerCard/components/UpdateConfirmationModal/UpdateConfirmationModal';
import {
  ManageProductType,
  SubscriptionManagerCardProps,
  SubscriptionManagerState,
} from '#component/page/Profile/components/ManagePlanTab/SubscriptionManager/SubscriptionManager.types';
import {
  getMembershipVariantId,
  mapProductsToVariants,
  useGetSubscriptionManagerCardData,
} from '#component/page/Profile/components/ManagePlanTab/SubscriptionManager/SubscriptionManager.util';
import {SkipNextOrderModal} from '#component/page/Profile/components/SubscriptionTab/CTASubscription/SkipNextOrderModal';
import {TESTKIT_RECUR_VARIANT_TID} from '#constant/products';
import {CustomerTypeEnum, SubscriptionStatusEnum} from '#util/subscriptions';

import {BenefitsList} from './components/BenefitsList';
import {HeaderSubscription} from './components/HeaderSubscription';
import {ProductRow} from './components/ProductRow';
import {PromoCodeField} from './components/PromoCodeField';
import {ProductVariantConfirmation} from './components/UpdateConfirmationModal/UpdateConfirmationModal.types';

import * as css from './SubscriptionManagerCard.css';

export const SubscriptionManagerCard = ({
  customerType,
  state,
  productVariants,
  initialState,
  pendingChanges,
  setPendingChanges,
  onChangeSubscription,
  onCancelPlan,
}: SubscriptionManagerCardProps) => {
  const {
    supplementTotalValue,
    supplementTotalValueDiscounted,
    membershipTotalValue,
    membershipTotalValueDiscounted,
    textColor,
    isDiscounted,
    title,
    activeSubscription,
    discount,
    selectedSupplements,
    onChangeSupplement,
    selectedSupplementsCount,
    undoChanges,
    supplementRenderList,
    startingAtValue,
    handleTrackClick,
    discountsToSimulate,
    openUpdateConfirmationModal,
    setOpenUpdateConfirmationModal,
  } = useGetSubscriptionManagerCardData({customerType, state, productVariants});

  const {
    hasActiveSubscription,
    hasMembership,
    subscriptionStatus,
    products: manageProducts = [],
    addProducts,
    removeProducts,
    nextBillingDateAt = '',
    subscriptionId = '',
  } = pendingChanges || {};
  const hasActiveSubscriptionOrMembership =
    (hasActiveSubscription ||
      hasMembership ||
      subscriptionStatus === SubscriptionStatusEnum.PAUSED) &&
    !(subscriptionStatus === SubscriptionStatusEnum.CANCELLED);

  const [addProductVariantIds, setAddProductVariantIds] = useState(addProducts ?? []);
  const [removeProductVariantIds, setRemoveProductVariantIds] = useState(removeProducts ?? []);
  const [productsWithTid, setProductsWithTid] = useState<ProductVariantConfirmation[]>(
    mapProductsToVariants(manageProducts, productVariants),
  );

  const {data} = useManagePlanQuery();
  const {subscription} = data?.me?.customerAccount ?? {};

  useEffect(() => {
    if (addProducts) {
      setAddProductVariantIds(addProducts);
    }
    if (removeProducts) {
      setRemoveProductVariantIds(removeProducts);
    }
    if (manageProducts) {
      const manageProductsSet = new Set(manageProducts.map(product => product.variantId));
      const filteredManageProducts = Array.from(manageProductsSet).map(variantId =>
        manageProducts.find(product => product.variantId === variantId),
      ) as ManageProductType[];
      setProductsWithTid(mapProductsToVariants(filteredManageProducts, productVariants));
    }
  }, [addProducts, productVariants, manageProducts, removeProducts]);

  const handleOpenUpdateConfirmationModal = () => {
    handleTrackClick('Manage My Plan - Click Confirm Change');
    if (!hasMembership) {
      const testKitRecurring = manageProducts.find(
        product => product.tid === TESTKIT_RECUR_VARIANT_TID,
      );
      if (testKitRecurring && !removeProductVariantIds.includes(testKitRecurring.variantId)) {
        setRemoveProductVariantIds([...removeProductVariantIds, testKitRecurring.variantId]);
      }
      const filteredManageProducts = Array.from(
        new Set(
          manageProducts
            .filter(product => product.category === ProductCategoryEnum.Supplement)
            .map(product => product.variantId),
        ),
      ).map(variantId => manageProducts.find(product => product.variantId === variantId)) as
        | ManageProductType[];

      setProductsWithTid(mapProductsToVariants(filteredManageProducts, productVariants));
    }
    setOpenUpdateConfirmationModal(true);
  };

  const handleCancelPendingStatus = () => {
    const memberVariantId = getMembershipVariantId(productVariants);
    const memberVariant = productVariants.find(v => v.id === memberVariantId);

    if (memberVariantId && memberVariant && !addProductVariantIds.includes(memberVariantId)) {
      const newAddProductVariantIds = [...addProductVariantIds, memberVariantId];
      const newProductsWithTid = [
        ...productsWithTid,
        {
          tid: memberVariant.tid,
          id: memberVariant.id,
          variantId: memberVariant.id,
          price: memberVariant.price,
          imageURL: memberVariant.product?.thumbnail || '',
          title: memberVariant.title,
        } as ProductVariantConfirmation,
      ];

      setAddProductVariantIds(newAddProductVariantIds);
      setProductsWithTid(newProductsWithTid);
    }
    handleOpenUpdateConfirmationModal();
  };

  let headerTotalValue = membershipTotalValue;
  let headerTotalValueDiscounted = membershipTotalValueDiscounted.toFixed(2);
  if (customerType.toLowerCase() !== CustomerTypeEnum.membership) {
    headerTotalValue = supplementTotalValue;
    headerTotalValueDiscounted = supplementTotalValueDiscounted.toFixed(2);
  }

  const formattedNextBillingDate =
    nextBillingDateAt && format(new Date(nextBillingDateAt), 'MMMM do, yyyy');

  return (
    <div
      className={cx(
        css.SubscriptionCardContainer({
          state: hasActiveSubscriptionOrMembership ? state : 'bothDeselected',
        }),
      )}>
      {activeSubscription && (
        <Text
          variant="label"
          className={css.StatusTag({
            state,
          })}>
          {state}
        </Text>
      )}
      <HeaderSubscription
        title={title}
        textColor={textColor}
        isDiscounted={isDiscounted}
        state={state}
        totalValue={headerTotalValue}
        totalValueWithDiscount={headerTotalValueDiscounted}
        startingAtValue={startingAtValue}
      />
      <BenefitsList customerType={customerType} />
      {activeSubscription && (
        <>
          <Text className={css.SupplementListHeader} as="h4" variant="body" color="burgundy">
            My Supplements
          </Text>
          {supplementRenderList.map(supplement => (
            <ProductRow
              key={supplement.variantId}
              image={supplement.imageURL}
              title={supplement.title}
              value={supplement.price}
              discount={discount}
              subscribed={!!selectedSupplements[supplement.variantId]}
              membershipIncluded={!!supplement.membershipIncluded}
              onChange={onChangeSupplement(
                supplement,
                productVariants,
                customerType,
                subscription || {},
              )}
            />
          ))}
        </>
      )}
      {activeSubscription && <PromoCodeField />}
      {selectedSupplementsCount === 0 && state === SubscriptionManagerState.UPDATE && (
        <Text className={css.SkipCaption} as="p" variant="caption" color="doveGray">
          Need a break from a supplement? No problem! You can simply skip your next order.
        </Text>
      )}
      {state === SubscriptionManagerState.PAUSED && (
        <div>
          <Button as="a" variant="primary" onClick={handleOpenUpdateConfirmationModal}>
            Reactivate
          </Button>
        </div>
      )}
      {state === SubscriptionManagerState.PENDING && formattedNextBillingDate && (
        <div className={css.PendingSpanWrapper}>
          <Text className={css.PendingSpan} as="span" variant="caption" color="doveGray">
            Your Membership will remain active until{' '}
            <Text
              className={css.PendingSpan}
              as="span"
              variant="caption"
              color="doveGray"
              fontWeight="bold">
              {formattedNextBillingDate}
            </Text>
            , at which point this pending plan will go into effect. You can cancel this pending
            change and return to your previous Membership settings by{' '}
            <Link className={css.PendingLinkButton} as="button" onClick={handleCancelPendingStatus}>
              clicking here
            </Link>
            .
          </Text>
        </div>
      )}
      {state === SubscriptionManagerState.UPDATE && (
        <>
          {selectedSupplementsCount > 0 ? (
            <div>
              <Button as="a" variant="primary" onClick={handleOpenUpdateConfirmationModal}>
                Confirm Change
              </Button>
            </div>
          ) : (
            <SkipNextOrderModal
              productVariants={productVariants}
              skipBtnVariant="secondary"
              buttonLabel="Skip Supplements"
              className={css.SwitchPlanButton}
            />
          )}
          {selectedSupplementsCount === 0 && (
            <Button
              as="a"
              variant="secondary"
              className={css.SwitchPlanButton}
              onClick={onCancelPlan}>
              Cancel My Plan
            </Button>
          )}
          <Button as="a" variant="secondary" className={css.SwitchPlanButton} onClick={undoChanges}>
            Undo Changes
          </Button>
        </>
      )}
      {state === SubscriptionManagerState.DESELECTED && hasActiveSubscriptionOrMembership && (
        <Button
          as="a"
          variant="secondary"
          className={css.SwitchPlanButton}
          onClick={() => {
            handleTrackClick('Manage My Plan - Click Switch Plan');
            if (initialState) {
              onChangeSubscription({
                hasMembership: !!(customerType.toLowerCase() === CustomerTypeEnum.membership),
                initialState,
                setPendingChanges,
                productVariants,
                customerType,
              });
            }
          }}>
          Switch Plan
        </Button>
      )}
      {customerType.toLowerCase() === CustomerTypeEnum.subscription &&
        !hasActiveSubscriptionOrMembership && (
          <NextLink href="/shop/collections/supplements" passHref>
            <Button
              as="a"
              variant="secondary"
              className={css.SwitchPlanButton}
              onClick={() => handleTrackClick('Manage My Plan - Shop')}>
              Shop Supplements
            </Button>
          </NextLink>
        )}
      {customerType.toLowerCase() === CustomerTypeEnum.membership &&
        !hasActiveSubscriptionOrMembership && (
          <NextLink href="/shop/products/membership" passHref>
            <Button
              as="a"
              variant="secondary"
              className={css.SwitchPlanButton}
              onClick={() => handleTrackClick('Manage My Plan - Shop')}>
              Shop Membership
            </Button>
          </NextLink>
        )}
      {openUpdateConfirmationModal && (
        <UpdateConfirmationModal
          open={openUpdateConfirmationModal}
          onOpenChange={setOpenUpdateConfirmationModal}
          addProductVariants={addProductVariantIds}
          removeProductVariants={removeProductVariantIds}
          nextBillingDateAt={nextBillingDateAt}
          productVariants={productsWithTid}
          subscriptionId={parseInt(subscriptionId, 10)}
          subscriptionStatus={subscriptionStatus || ''}
          allProductVariants={productVariants}
          discountsToSimulate={discountsToSimulate ?? []}
          subscriptionItems={subscription?.items ?? []}
        />
      )}
    </div>
  );
};
